import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VehicleForm, { resolveImg } from "./VehicleForm";
import { Pencil, Trash2, Plus, Star } from "lucide-react";

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | vehicle | "new"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vehicles");
      setVehicles(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (v) => {
    if (!window.confirm(`Delete ${v.make} ${v.model}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/vehicles/${v.id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const price = (n) => new Intl.NumberFormat("en-GH", { style: "currency", currency: "GHS", maximumFractionDigits: 0 }).format(n);

  return (
    <div data-testid="admin-vehicles">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline text-[#C5A059] mb-2">Vehicles</div>
          <div className="font-display text-3xl">The Collection · Live</div>
          <div className="text-white/50 text-sm mt-1">{vehicles.length} vehicle{vehicles.length !== 1 ? "s" : ""} currently on the site.</div>
        </div>
        <button onClick={()=>setEditing("new")} className="btn-editorial btn-editorial-gold" data-testid="new-vehicle-btn">
          <Plus size={14}/> New Vehicle
        </button>
      </div>

      {loading ? (
        <div className="text-white/50 py-10 text-center">Loading…</div>
      ) : vehicles.length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-white/50">
          No vehicles yet. Click New Vehicle to publish your first.
        </div>
      ) : (
        <div className="grid gap-4">
          {vehicles.map((v) => (
            <div key={v.id} className="grid grid-cols-12 gap-4 items-center border border-white/10 p-4 hover:border-white/25 transition" data-testid={`vehicle-row-${v.slug}`}>
              <div className="col-span-2">
                <div className="aspect-video overflow-hidden bg-[#0A1128]">
                  {v.images?.[0] && <img src={resolveImg(v.images[0])} alt="" className="w-full h-full object-cover"/>}
                </div>
              </div>
              <div className="col-span-5">
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">{v.year} · {v.make}</div>
                <div className="font-display text-2xl mt-1 flex items-center gap-2">
                  {v.model}
                  {v.featured && <Star size={14} className="text-[#C5A059]" fill="#C5A059"/>}
                </div>
                <div className="text-white/50 text-sm">{v.type} · {v.mileage_km?.toLocaleString?.()} km</div>
              </div>
              <div className="col-span-2 text-white font-display">{price(v.price_ghs)}</div>
              <div className="col-span-1 text-white/50 text-xs">{v.images?.length || 0} pics</div>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={()=>setEditing(v)} className="btn-editorial" data-testid={`edit-${v.slug}`}><Pencil size={13}/> Edit</button>
                <button onClick={()=>del(v)} className="text-white/60 hover:text-red-400 border border-white/15 hover:border-red-400/50 px-3 py-2" data-testid={`delete-${v.slug}`}><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <VehicleForm
          initial={editing === "new" ? null : editing}
          onClose={()=>setEditing(null)}
          onSaved={()=>{ setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
